import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function InsightsGrid() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="rounded-lg bg-card border border-border overflow-hidden">
            <div className="aspect-[4/5] bg-muted/40 animate-pulse" />
            <div className="p-7 space-y-3">
              <div className="h-3 w-16 bg-muted/40 rounded animate-pulse" />
              <div className="h-5 w-3/4 bg-muted/40 rounded animate-pulse" />
              <div className="h-4 w-full bg-muted/40 rounded animate-pulse" />
              <div className="h-4 w-2/3 bg-muted/40 rounded animate-pulse" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-16">
        <p className="text-muted-foreground">Unable to load articles at the moment.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-16">
        <p className="text-muted-foreground">No insights available yet. Check back soon.</p>
      </div>;
  }
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    } catch {
      return "";
    }
  };
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const categoryLabel = post.categories && post.categories.length > 0 ? post.categories[0].name : "";
      return <Link key={post.id} to={post.link || "#"} data-index={i} className="group block">
            <article className="h-full flex flex-col rounded-lg bg-card border border-border overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 ease-in-out">
              <div className="relative aspect-[4/5] overflow-hidden bg-muted/30">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105" /> : <div className="absolute inset-0 bg-accent" />}
              </div>

              <div className="flex flex-col flex-1 p-7">
                {categoryLabel && <span className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground mb-4">
                    {categoryLabel}
                  </span>}

                <h3 className="font-default text-xl font-medium text-foreground leading-snug tracking-tight mb-3">
                  {post.title}
                </h3>

                {post.excerpt && <p className="text-sm text-muted-foreground leading-relaxed mb-6 overflow-hidden" style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical"
            }} dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}

                <div className="mt-auto pt-4 border-t border-border">
                  <time className="text-xs tracking-wide text-muted-foreground">
                    {formatDate(post.date)}
                  </time>
                </div>
              </div>
            </article>
          </Link>;
    })}
    </div>;
}
export default function Thinking() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="13674" id="latest-thinking" className="relative overflow-hidden bg-background light" style={{
    paddingTop: "clamp(96px, 12vh, 160px)",
    paddingBottom: "clamp(96px, 12vh, 160px)"
  }}>
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/4 -left-1/4 w-[80%] h-[80%] rounded-lg opacity-20 blur-3xl" style={{
        background: "radial-gradient(circle at center, var(--primary) 0%, transparent 65%)"
      }} />
      </div>

      <div className="mx-auto w-full max-w-[1280px]" style={{
      paddingLeft: "clamp(20px, 4vw, 56px)",
      paddingRight: "clamp(20px, 4vw, 56px)"
    }}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-default text-foreground" style={{
            fontSize: "clamp(2rem, 3.8vw, 3.5rem)",
            fontWeight: 500,
            letterSpacing: "-0.02em",
            lineHeight: 1.1
          }}>
              Strategic <span className="font-serif italic font-light">perspectives</span>
            </h2>
          </div>

          <div className="lg:col-span-4 flex lg:justify-end">
            <Button asChild variant="link" className="group h-auto p-0 text-foreground hover:no-underline">
              <Link to="/InsightsPage" className="inline-flex items-center gap-2 text-base font-medium">
                <span className="relative">
                  View all articles
                </span>
                <ArrowRight className="size-4 text-primary transition-transform duration-300 ease-in-out group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>

        <div style={{
        marginTop: "clamp(48px, 7vh, 80px)"
      }}>
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date"
        }}>
            <InsightsGrid />
          </PostsProvider>
        </div>
      </div>
    </section>;
}